import type { UseMutationResult, UseQueryResult } from "react-query";
import { useMutation, useQuery } from "react-query";
import I18n from "@/i18n-js/instance";
import {
  reactQueryGet,
  reactQueryPost,
  reactQueryPut,
} from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import type { PaywallGroupProps } from "@/react/types/PaywallGroup";
import type { ApiError } from "@circle-react/config/CustomErrors";
import { useToast } from "@circle-react-uikit/ToastV2";

interface PaywallGroupFormData {
  currency_id?: number;
  id?: number;
  name: string;
}

interface UsePaywallGroupApiParams {
  closeModal?: (() => void) | null;
  refetchPaywallGroups?: (() => void) | null;
}

interface UsePaywallGroupApiReturn {
  createMutation: UseMutationResult<
    PaywallGroupProps,
    ApiError,
    PaywallGroupFormData
  >;
  updateMutation: UseMutationResult<
    PaywallGroupProps,
    ApiError,
    PaywallGroupFormData
  >;
  useGetPaywallGroup: (
    paywallGroupId: string,
  ) => UseQueryResult<PaywallGroupProps, ApiError>;
  useListPaywallGroups: (
    params?: any,
  ) => UseQueryResult<PaywallGroupProps[], ApiError>;
}

export const usePaywallGroupApi = ({
  closeModal = null,
  refetchPaywallGroups = null,
}: UsePaywallGroupApiParams): UsePaywallGroupApiReturn => {
  const INDEX_REQUEST_KEY = "paywall-groups";
  const SHOW_REQUEST_KEY = "paywall-group-show";
  const { success, error } = useToast();

  const useListPaywallGroups = (
    params?: any,
  ): UseQueryResult<PaywallGroupProps[], ApiError> => {
    const queryFn = () =>
      reactQueryGet(internalApi.paywallGroup.index({ params: params }));

    return useQuery(INDEX_REQUEST_KEY, queryFn);
  };

  const useGetPaywallGroup = (
    paywallGroupId: string,
  ): UseQueryResult<PaywallGroupProps, ApiError> => {
    const queryFn = () =>
      reactQueryGet(
        internalApi.paywallGroup.show({
          paywallGroupId: paywallGroupId,
        }),
      );

    return useQuery(SHOW_REQUEST_KEY, queryFn);
  };

  const updateMutation: UseMutationResult<
    PaywallGroupProps,
    ApiError,
    PaywallGroupFormData
  > = useMutation(
    (formData: PaywallGroupFormData) =>
      reactQueryPut(
        internalApi.paywallGroup.update({
          paywallGroupId: formData.id,
        }),
        { paywall_group: formData },
      ),
    {
      onSuccess: () => {
        success(I18n.t("settings.paywall_groups.alerts.success.updated"));
        closeModal?.();
        refetchPaywallGroups?.();
      },
      onError: (updateError: ApiError) => {
        console.error(updateError.errorDetails);
        // eslint-disable-next-line @typescript-eslint/no-base-to-string -- Updating typescript-eslint, should be fixed when appropriate
        error(String(updateError.errorDetails || updateError.message), {
          duration: "long",
        });
      },
    },
  );

  const createMutation: UseMutationResult<
    PaywallGroupProps,
    ApiError,
    PaywallGroupFormData
  > = useMutation(
    (formData: PaywallGroupFormData) =>
      reactQueryPost(internalApi.paywallGroup.create(), {
        paywall_group: formData,
      }),
    {
      onSuccess: () => {
        success(I18n.t("settings.paywall_groups.alerts.success.created"));
        closeModal?.();
        refetchPaywallGroups?.();
      },
      onError: (createError: ApiError) => {
        console.error(createError.errorDetails);
        // eslint-disable-next-line @typescript-eslint/no-base-to-string -- Updating typescript-eslint, should be fixed when appropriate
        error(String(createError.errorDetails || createError.message), {
          duration: "long",
        });
      },
    },
  );

  return {
    useListPaywallGroups,
    updateMutation,
    createMutation,
    useGetPaywallGroup,
  };
};
